import React, { Component, Children } from 'react'
import isEqual from 'lodash/isEqual'
import isString from 'lodash/isString'
import PropTypes from 'prop-types'

import { bind, getExtraneousProps } from '../../util/react'

const formPropTypes = {
    className: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    error: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    model: PropTypes.any,
    onChange: PropTypes.func,
    // Do not manually pass onModelChange, provided automatically by root form
    onModelChange: PropTypes.func,
    onSubmit: PropTypes.func,
    partial: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

const formDefaultProps = {
    onChange: (model, prevModel) => {},
    onModelChange: () => {},
    onSubmit: (model, prevModel) => {}
}

const formContextTypes = {
    bindTo: PropTypes.func,
    // eslint-disable-next-line react/forbid-prop-types
    error: PropTypes.any,
    // eslint-disable-next-line react/forbid-prop-types
    model: PropTypes.any,
    onModelChange: PropTypes.func
}

/**
 * Generic form that supports complex models representation through partial form instances.
 * The models or its properties can be objects, lists of objects/primitives or primitives themselves (string or number)
 *
 * NOTE: The fields should go in a sub-component, not directly in the form, since they must receive bound model
 * from the form. The Form component should only define the form's structure.
 * An alternative could be extending the Form class, but it's not worth the hustle.
 *
 * Please refer to the showcase for advanced usage!
 *
 * @example SimpleForm
 * const model = {
 *      lastName: 'Max',
 *      firstName: 'Mustermann',
 *      id: 123
 *  }
 *
 *  const forceNumber = (value, oldValue) => value.match(/^[\d]*$/) ? parseInt(value) : oldValue
 *
 *  const UserForm = ({ bindTo }) => (
 *      <div>
 *          <TextField {...bindTo('firstName')} />
 *          <TextField {...bindTo('lastName')} />
 *          <TextField {...bindTo('id', forceNumber)} />
 *          <button type='submit'>Submit</button>
 *      </div>
 *  )
 *
 * <Form model={model} onSubmit={(model) => {}}>
 *      <UserForm />
 * </Form>
 */
class BaseForm extends Component {
    constructor(props) {
        super(props)
        this.state = { model: null, shouldScroll: false }
        bind(this)
    }

    getChildContext() {
        return {
            bindTo: this.bindTo,

            /*
                When using the form iterator, every child of the model gets wrapped in a new
                form instance. Due to this the passed error is not on the props object anymore.
                So when a parent Baseform exists, the error should be taken from it's context.
             */
            error: this.props.error || (this.context && this.context.error) || null,
            model: this.state.model,
            onModelChange: this.onModelChange
        }
    }

    UNSAFE_componentWillMount() {
        this.setState({ model: this.props.model })
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.model !== nextProps.model) {
            this.setState({ model: nextProps.model })
        }
        if (nextProps.error && this.props.error !== nextProps.error) {
            this.setState({ shouldScroll: true })
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        // The model in the state might have changed to the same/previous value
        // through cloning, hence just reference-comparing is not an option.
        return this.props !== nextProps || !isEqual(this.state, nextState)
    }

    componentDidUpdate() {
        if (!this.props.partial && this.props.error && this.state.shouldScroll) {
            const fieldsWithErrors = document.querySelectorAll('[data-error-anchor="true"]')
            if (fieldsWithErrors.length > 0) {
                if (typeof fieldsWithErrors[0].scrollIntoView === 'function') {
                    fieldsWithErrors[0].scrollIntoView(false)
                }
            }
            this.setState({ shouldScroll: false }) // eslint-disable-line react/no-did-update-set-state
        }
    }

    render() {
        const { className, partial } = this.props
        const { model } = this.state
        // If the model is an array, we wrap each model item into a partial form and apply children to each of them
        const renderedChildren = Array.isArray(model) ? model.map(this.renderFormList) : this.renderChildren()
        // Wrap in form tag if it is a root form component. Use a normal div otherwise
        return !partial && partial !== 0 ? (
            <form noValidate className={className} onSubmit={this.onSubmit}>
                {renderedChildren}
            </form>
        ) : (
            <div>{renderedChildren}</div>
        )
    }

    /**
     * renderFormList - renders a partial form for the given item in the list.
     *
     * @param  {object} model in the array to be rendered
     * @param  {number} index of the model in the array
     * @return {object}       a rendered partial Form element
     */
    renderFormList(model, index) {
        const { children } = this.props
        return (
            <Form key={index} partial={index}>
                {children}
            </Form>
        )
    }

    /**
     * Renders the children by adding additional properties to each of them. Namely:
     * onModelChange (fieldName, value) - updates a field in the current model with the given value
     * bindTo (fieldName) - a shortcut to add props to an element in order to bind it to the current module,
     * errors - an object with model fields containing a list of errors, if any.
     */
    renderChildren() {
        const { model } = this.state
        const { children, error, partial } = this.props
        const { bindTo, onModelChange } = this

        const validChildren = Children.toArray(children)

        return Children.map(validChildren, child => {
            if (isString(child.type)) {
                return child // prevent unrelated props to be passed to HTML-elements ("type" of type string)
            }
            const newProps = { bindTo, error, model, onModelChange }
            if (typeof partial === 'number') {
                newProps.partialFormIndex = partial
            }
            return React.cloneElement(child, newProps)
        })
    }

    /**
     * bindTo - Returns an object that can be used to bind a certain model's field to any element. If no fieldName
     * is provided, it will bind the whole model. This is useful when the model is a primitive (string or number).
     *
     * @param  {(string|null)}  fieldName  of the field to bind. Null if binding the whole model in case of primitives
     * @param  {function} middleware       to pass the new value through before applying to the model
     * @return {object}                    object to be added as props to an element
     * @example
     * <input type='text' {...bindTo('name')} />
     */
    bindTo(fieldName, middleware) {
        const { model } = this.state
        const { error } = this.props
        const reasons = error && Array.isArray(error.reasons) ? error.reasons : []
        const fieldError = reasons.filter(error => error.fieldName === fieldName)[0]
        const props = {
            error: fieldError,
            name: fieldName,
            onChange: event => this.onModelChange(fieldName, event.target.value, middleware),
            value: fieldName ? model[fieldName] : model
        }

        if (fieldError) {
            props['data-error-anchor'] = true
        }

        return props
    }

    /**
     * onModelChange - trigger to update the model with the given value. If no target is provided, the whole model
     * is updated. This is the case, when the model is a primitive (string or number).
     *
     * If the current form is a partial, it automatically calls the parent form's onModeChange to update itself in a
     * bigger context.
     *
     * @param  {(string|null|object)} target   of the field to bind or an object of value to be changed. Null if binding
     *                                         the whole model.
     * @param  {*} value                       the new value to be changed in the model
     * @param  {function} middleware           to pass the new value through before applying
     */
    onModelChange(target, value, middleware) {
        let model = null
        let valueChangedByMiddlewareToPrevious = false
        const applyMidleware = (value, target) => {
            if (!middleware) {
                return value
            }
            const newValue = middleware(value, target)
            valueChangedByMiddlewareToPrevious = !isEqual(newValue, value) && isEqual(newValue, target)
            return newValue
        }
        if (typeof target === 'string') {
            // We are updating a field in a model that is an object
            value = applyMidleware(value, this.state.model[target])
            model = target ? { ...this.state.model, [target]: value } : value
        } else if (typeof this.state.model === 'string' || typeof this.state.model === 'number') {
            // We are updating the whole model which is a primitive
            model = applyMidleware(value, this.state.model)
        } else if (typeof target === 'object') {
            // if target is a object with some attributes as key value pair to be updated, update them to the model
            model = Object.assign({}, this.state.model, target)
        } else {
            // The model is an array, target is an index in this case, which can be used to update the array
            model = Array.from(this.state.model)
            model[target] = applyMidleware(value, model[target])
        }

        this.setState({ model }, () => {
            const { onChange, onModelChange } = this.props
            onChange(model)
            onModelChange(this.props.partial, model)
            if (valueChangedByMiddlewareToPrevious) {
                this.forceUpdate()
            }
        })
    }

    onSubmit(event) {
        event.preventDefault()
        this.props.onSubmit({ ...this.state.model })
    }
}

BaseForm.propTypes = formPropTypes
BaseForm.defaultProps = formDefaultProps
BaseForm.contextTypes = formContextTypes
BaseForm.childContextTypes = formContextTypes

/**
 * A wrapper for the BaseForm that handles where the error and model come from.
 * If it is a root form, those are loaded from the props. Otherwise from the context.
 */
const Form = (props, context) => {
    let { error } = props
    const { partial } = props
    const isRootForm = !partial && partial !== 0
    const model = isRootForm ? props.model || {} : context.model[partial]
    let onModelChange = () => {}

    if (!isRootForm) {
        const hasReasons = context.error && Array.isArray(context.error.reasons)
        error = hasReasons ? context.error.reasons.filter(error => error.fieldName === partial)[0] : null
        onModelChange = context.onModelChange
    }
    return <BaseForm {...props} {...{ error, model, onModelChange }} />
}
Form.propTypes = formPropTypes
Form.defaultProps = formDefaultProps
Form.contextTypes = formContextTypes
Form.Partial = PartialForm
Form.connect = connect

/**
 * A simple alternative to write form partials with a required partial property called name.
 *
 * Example:
 *     <Form ...>
 *         <Form.Partial name='details'>...</Form.Partial>
 *     </Form>
 */
export function PartialForm({ name, ...props }) {
    return <Form partial={name} {...props} />
}

PartialForm.propTypes = {
    className: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    error: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    model: PropTypes.any,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    // Do not manually pass onModelChange, provided automatically by root form
    onModelChange: PropTypes.func,
    onSubmit: PropTypes.func,
    partial: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

/**
 * Wraps the original component into a <Form> component.
 *
 * Example:
 *     const OriginalForm = ({ bindTo }) => <input {...bindTo('name')}>
 *     const MyForm = connect(OriginalForm)
 *
 *     <MyForm onSubmit={...} model={{ name: 'Max Mustermann' }}>
 */
export function connect(ComposedComponent) {
    return props => (
        <Form {...props}>
            <ComposedComponent {...getExtraneousProps(formPropTypes, props)} />
        </Form>
    )
}

export default Form
